import { useCallback } from 'react';
import { Transaction } from '../models/transaction';
import { useTradingAccounts } from '../api/trading-accounts/use-trading-accounts';
import { useQuery, useMutation } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../api/httpClient';
import { API_ENDPOINTS } from '../api/endpoints';
import { Instrument } from '../api/types';

// Define wallet balance interface locally since it's not exported from models
interface WalletBalance {
    id: string;
    currency: string;
    symbol: string;
    balance: number;
    available: number;
    locked: number;
    usdValue: number;
    icon?: string;
}

// Define deposit address interface locally
interface DepositAddress {
    currency: string;
    address: string | null;
    tag: string | null;
    qrCode: string | null;
}

// Define withdrawal request interface locally
interface WithdrawalRequest {
    currency: string;
    amount: number;
    address: string;
}

export const useWallet = (quoteCurrency: string = 'USD') => {
    // Get all trading accounts instead of wallets
    const { data: tradingAccounts, isLoading: isLoadingWallets } = useTradingAccounts();

    // Get instruments for price calculation based on quote currency
    const { data: instruments = [] } = useQuery<Instrument[]>({
        queryKey: ['instruments', quoteCurrency],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Instrument[]>>(
                `${API_ENDPOINTS.TRADER.INSTRUMENTS}`
            );
            // Filter to show instruments with the specified quote currency
            return data.data.filter(instrument => instrument.quote_asset.symbol === quoteCurrency);
        },
    });

    // Transform trading accounts into WalletBalance format with quote currency values
    const balances: WalletBalance[] =
        tradingAccounts?.accounts?.map(account => {
            // Find price instrument for this currency
            const quoteInstrument = instruments.find(
                instrument =>
                    instrument.base_asset.symbol === account.base_currency &&
                    instrument.quote_asset.symbol === quoteCurrency
            );

            const quoteValue = quoteInstrument
                ? account.balance * (quoteInstrument.current_price || 0)
                : account.balance;

            return {
                id: account.id,
                currency: account.base_currency,
                symbol: account.base_currency,
                balance: account.balance,
                available: account.free_margin,
                locked: account.used_margin,
                usdValue: quoteValue, // Keep the property name for backward compatibility
                icon: undefined, // TODO: Add currency icons
            };
        }) || [];

    // Get transactions
    const { data: transactions = [] } = useQuery<Transaction[]>({
        queryKey: ['wallet-transactions'],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<Transaction[]>>(
                `${API_ENDPOINTS.TRADER.TRANSACTIONS.LIST}`
            );
            return data.data;
        },
    });

    // Deposit mutation - now uses trading account deposit
    const { mutate: deposit, isPending: isDepositing } = useMutation({
        mutationFn: (data: any) => {
            // This will be handled by the trading account deposit form
            console.log('Deposit to trading account:', data);
            return Promise.resolve();
        },
    });

    // Withdraw mutation - now uses trading account withdrawal
    const { mutate: withdraw, isPending: isWithdrawing } = useMutation({
        mutationFn: (data: any) => {
            // This will be handled by the trading account withdrawal form
            console.log('Withdraw from trading account:', data);
            return Promise.resolve();
        },
    });

    const handleDeposit = useCallback(
        (currency: string) => {
            const account = tradingAccounts?.accounts?.find(a => a.base_currency === currency);
            if (!account) return null;

            return {
                currency: account.base_currency,
                address: null, // Trading accounts don't have addresses
                tag: null,
                qrCode: null,
            } as DepositAddress;
        },
        [tradingAccounts]
    );

    const handleWithdraw = useCallback(
        (request: WithdrawalRequest) => {
            withdraw({
                currency: request.currency,
                amount: request.amount,
                address: request.address,
            });
        },
        [withdraw]
    );

    const getTransactionHistory = useCallback(
        (currency?: string) => {
            if (!currency) return transactions;
            return transactions.filter(tx => tx.currency === currency);
        },
        [transactions]
    );

    // Calculate total portfolio value in quote currency
    const totalPortfolioValue = balances.reduce((total, balance) => total + balance.usdValue, 0);

    return {
        balances,
        transactions,
        isLoadingWallets,
        isDepositing,
        isWithdrawing,
        handleDeposit,
        handleWithdraw,
        getTransactionHistory,
        totalPortfolioValue,
    };
};
